/**
 * Shared annotations: small spheres in the author's role colour at the point someone marked,
 * with the note on hover. Everyone in the room sees them; the author (or anyone) can remove
 * one by selecting it. One instanced mesh.
 */
import * as THREE from 'three';
import { ROLE_STYLES, type Annotation } from '@clinspace/shared';
import type { InteractiveSystem } from '../ui/Interactive.ts';

const MAX = 200;

export class Annotations {
  readonly mesh: THREE.InstancedMesh;
  private items: Annotation[] = [];
  private hovered = -1;

  constructor(
    interactive: InteractiveSystem,
    private readonly handlers: {
      tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
      select(annotation: Annotation, at: THREE.Vector3): void;
    },
  ) {
    this.mesh = new THREE.InstancedMesh(
      new THREE.SphereGeometry(0.03, 12, 10),
      new THREE.MeshStandardMaterial({
        roughness: 0.3,
        metalness: 0.1,
        emissive: 0xffffff,
        emissiveIntensity: 0.3,
      }),
      MAX,
    );
    this.mesh.name = 'annotations';
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    interactive.add({
      object: this.mesh,
      onHover: (_p, point, hit) => {
        const i = hit?.instanceId ?? -1;
        if (i === this.hovered) return;
        this.hovered = i;
        const a = this.items[i];
        handlers.tooltip(
          a
            ? [
                `${a.author.name} (${ROLE_STYLES[a.author.role].short}) · ${a.at.slice(0, 16).replace('T', ' ')}`,
                a.text,
                'Select to remove this note',
              ]
            : undefined,
          point,
        );
      },
      onLeave: () => {
        this.hovered = -1;
        handlers.tooltip(undefined, undefined);
      },
      onSelect: (_p, point, hit) => {
        const a = this.items[hit?.instanceId ?? -1];
        if (a) handlers.select(a, point);
      },
    });
  }

  get count(): number {
    return this.items.length;
  }

  set(items: Annotation[]): void {
    this.items = items.slice(0, MAX);
    const dummy = new THREE.Object3D();
    const colour = new THREE.Color();
    this.items.forEach((a, i) => {
      dummy.position.set(a.position.x, a.position.y, a.position.z);
      dummy.updateMatrix();
      this.mesh.setMatrixAt(i, dummy.matrix);
      this.mesh.setColorAt(i, colour.set(ROLE_STYLES[a.author.role].hex));
    });
    this.mesh.count = this.items.length;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
    this.mesh.computeBoundingSphere();
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
